/**
 * @description Estimate list component with filtering, statistics, pagination, and actions.
 * Mirrors InvoiceList.js patterns adapted for the estimate lifecycle.
 *
 * Key features:
 * - Statistics cards: Draft, Sent, Accepted counts/amounts + Win Rate percentage
 * - Filters: customer dropdown, date range presets, status filter, search box
 * - Desktop card layout and mobile card layout with responsive breakpoints
 * - Row actions: View, Edit (draft only), Convert to Invoice, Delete
 * - Confirmation modals for destructive/conversion actions
 * - Pagination with Previous/Next controls
 *
 * @dependencies
 * - estimateService: CRUD, status actions, conversion, statistics
 * - estimateHelpers: status helpers, expiration checks, conversion eligibility
 * - invoiceHelpers: formatInvoiceDate (shared date formatting)
 * - customerHelpers: getCustomerDisplayName
 * - customersService: fetch customer list for filter dropdown
 * - StatusBadge: unified status rendering (type="estimate_status")
 * - PermissionGate / PERMISSIONS: RBAC gating for actions
 *
 * @notes
 * - No payment-related columns or actions (estimates don't track payments)
 * - Expiration date column replaces Due Date from InvoiceList
 * - Convert to Invoice replaces Void action from InvoiceList
 * - Status filter excludes "Converted" (user requirement)
 * - Win Rate = accepted / (accepted + declined) * 100
 */

import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { format } from 'date-fns';
import Spinner from '../shared/Spinner';
import { useUser } from '../../contexts/AuthContext';
import estimateService from '../../services/api/estimateService';
import { customersService } from '../../services/api';
import {
  formatInvoiceDate
} from '../../utils/invoiceHelpers';
import {
  isEstimateExpired,
  isEstimateEditable,
  canConvertToInvoice
} from '../../utils/estimateHelpers';
import { getCustomerDisplayName } from '../../utils/customerHelpers';
import StatusBadge from '../shared/StatusBadge';
import PermissionGate from '../shared/PermissionGate';
import { PERMISSIONS } from '../../constants/permissionMappings';

function EstimateList() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [customers, setCustomers] = useState({});
  const [statistics, setStatistics] = useState({
    draftCount: 0,
    draftAmount: 0,
    sentCount: 0,
    sentAmount: 0,
    acceptedCount: 0,
    acceptedAmount: 0,
    declinedCount: 0,
    winRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    customerId: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    search: '',
    datePreset: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    totalItems: 0,
    totalPages: 0
  });
  const [showCustomDatesModal, setShowCustomDatesModal] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ from: '', to: '' });
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [isDatePresetDropdownOpen, setIsDatePresetDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const customerDropdownRef = useRef(null);
  const datePresetDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const actionMenuRefs = useRef({});

  // Fetch customers for filter dropdown
  const fetchCustomers = useCallback(async () => {
    if (!user?.id) return;

    try {
      const customersData = await customersService.getMerchantCustomers();

      // Create customer lookup map
      const customerMap = {};
      if (customersData && Array.isArray(customersData)) {
        customersData.forEach(customer => {
          customerMap[customer.id] = customer;
        });
      }
      setCustomers(customerMap);
    } catch (err) {
      // Error handled via UI state
    }
  }, [user?.id]);

  // Fetch estimates with filters
  const fetchEstimates = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const response = await estimateService.getEstimates(params);

      const estimateList = response.success ? response.estimates : response.estimates || [];
      const processedEstimates = estimateList.map(est => ({
        ...est,
        subtotal: parseFloat(est.subtotal) || 0,
        totalAmount: parseFloat(est.totalAmount) || 0,
        // Prioritize company name, fallback to personal name, then email
        customerDisplayName: est.company || [est.firstName, est.lastName].filter(Boolean).join(' ') || est.email || 'Unknown Customer'
      }));

      setEstimates(processedEstimates);
      const totalCount = response.totalCount || 0;
      const currentPage = response.page || 1;
      const pageLimit = response.limit || 25;
      setPagination({
        page: currentPage,
        limit: pageLimit,
        totalItems: totalCount,
        totalPages: Math.ceil(totalCount / pageLimit)
      });
    } catch (err) {
      // Error handled via UI state
      setError('Failed to load estimates. Please try again.');
      setEstimates([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, filters, pagination.page, pagination.limit]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    if (!user?.id) return;

    try {
      const stats = await estimateService.getStatistics();

      const counts = stats.counts || {};
      const amounts = stats.amounts || {};

      const acceptedCount = counts.accepted || 0;
      const declinedCount = counts.declined || 0;
      const totalDecided = acceptedCount + declinedCount;
      const winRate = totalDecided > 0 ? Math.round((acceptedCount / totalDecided) * 100) : 0;

      setStatistics({
        draftCount: counts.draft || 0,
        draftAmount: (amounts.totalDraftCents || 0) / 100,
        sentCount: counts.sent || 0,
        sentAmount: (amounts.totalSentCents || 0) / 100,
        acceptedCount: acceptedCount,
        acceptedAmount: (amounts.totalAcceptedCents || 0) / 100,
        declinedCount: declinedCount,
        winRate: winRate
      });
    } catch (err) {
      // Error handled via UI state
    }
  }, [user?.id]);

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchCustomers(),
        fetchStatistics(),
        fetchEstimates()
      ]);
    };
    loadData();
  }, [fetchCustomers, fetchStatistics, fetchEstimates]);

  // Add click-outside handlers for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setIsCustomerDropdownOpen(false);
      }
      if (datePresetDropdownRef.current && !datePresetDropdownRef.current.contains(event.target)) {
        setIsDatePresetDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
      // Handle action menu dropdowns
      if (openActionMenuId) {
        const menuRef = actionMenuRefs.current[openActionMenuId];
        if (menuRef && !menuRef.contains(event.target)) {
          setOpenActionMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionMenuId]);

  // Clean customer display - prioritize company name
  const getCustomerName = (estimate) => {
    return estimate.customerDisplayName || 'Unknown Customer';
  };

  // Helper to get customer company line for table display
  const getCustomerCompany = (estimate) => {
    // If the display name is the company, return the personal name as secondary
    const personalName = [estimate.firstName, estimate.lastName].filter(Boolean).join(' ');
    if (estimate.company && personalName) {
      return personalName;
    }
    return estimate.email || '';
  };

  // Helper to get customer display for filter dropdown
  const getFilterCustomerDisplay = (customerId) => {
    if (!customerId) return 'All Customers';

    const customer = Object.values(customers).find(c =>
      c.id === customerId
    );

    if (!customer) return 'Unknown Customer';

    return getCustomerDisplayName(customer);
  };

  // Get display label for status filter
  const getStatusFilterDisplay = (status) => {
    if (!status) return 'All Statuses';
    const statusLabels = {
      'draft': 'Draft',
      'sent': 'Sent',
      'accepted': 'Accepted',
      'declined': 'Declined',
      'expired': 'Expired'
    };
    return statusLabels[status] || 'All Statuses';
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle date range presets
  const handleDatePreset = (preset) => {
    const today = new Date();
    const formatDate = (date) => format(date, 'yyyy-MM-dd');

    switch (preset) {
      case 'today':
        setFilters(prev => ({
          ...prev,
          dateFrom: formatDate(today),
          dateTo: formatDate(today)
        }));
        break;
      case 'week': {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        setFilters(prev => ({
          ...prev,
          dateFrom: formatDate(weekStart),
          dateTo: formatDate(today)
        }));
        break;
      }
      case 'month': {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        setFilters(prev => ({
          ...prev,
          dateFrom: formatDate(monthStart),
          dateTo: formatDate(today)
        }));
        break;
      }
      case 'quarter': {
        const quarter = Math.floor(today.getMonth() / 3);
        const quarterStart = new Date(today.getFullYear(), quarter * 3, 1);
        setFilters(prev => ({
          ...prev,
          dateFrom: formatDate(quarterStart),
          dateTo: formatDate(today)
        }));
        break;
      }
      case 'year': {
        const yearStart = new Date(today.getFullYear(), 0, 1);
        setFilters(prev => ({
          ...prev,
          dateFrom: formatDate(yearStart),
          dateTo: formatDate(today)
        }));
        break;
      }
      default:
        break;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const handleDuplicateEstimate = async (estimate) => {
    try {
      const result = await estimateService.cloneEstimate(estimate.estimateNumber);
      if (result?.estimate?.estimateNumber) {
        navigate(`/merchant/estimates/${result.estimate.estimateNumber}/edit`);
      }
    } catch (err) {
      console.error('Error duplicating estimate:', err);
      setError('Failed to duplicate estimate. Please try again.');
    }
  };

  // Handle action clicks that require confirmation
  const handleActionClick = (action, estimate) => {
    setSelectedEstimate(estimate);
    setConfirmAction(action);
    setShowConfirmModal(true);
    setOpenActionMenuId(null);
  };

  const handleConfirmAction = async () => {
    if (!selectedEstimate || !confirmAction) return;

    try {
      setActionLoading(true);

      if (confirmAction === 'convert') {
        await estimateService.convertToInvoice(selectedEstimate.estimateNumber);
      } else if (confirmAction === 'delete') {
        await estimateService.deleteEstimate(selectedEstimate.estimateNumber);
      }

      // Refresh the estimate list and statistics
      await Promise.all([
        fetchEstimates(),
        fetchStatistics()
      ]);

      // Close modal
      setShowConfirmModal(false);
      setSelectedEstimate(null);
      setConfirmAction(null);
    } catch (err) {
      // Error handled via UI state
      setError(`Failed to ${confirmAction === 'convert' ? 'convert' : 'delete'} estimate. Please try again.`);
    } finally {
      setActionLoading(false);
    }
  };

  // Render status badge for estimate
  const renderStatusBadge = (estimate) => {
    const expired = isEstimateExpired(estimate.status, estimate.expirationDate);
    const displayStatus = expired ? 'expired' : estimate.status;

    return (
      <StatusBadge
        type="estimate_status"
        value={displayStatus}
        size="xs"
      />
    );
  };

  // Render mobile estimate card
  const renderMobileEstimateCard = (estimate) => {
    const isDraft = isEstimateEditable(estimate.status);
    const canConvert = canConvertToInvoice(estimate.status);

    return (
      <div key={estimate.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex flex-wrap gap-4 flex-1">
            {/* First Row - Estimate Number and Customer Name */}
            <div className="flex-1 min-w-[45%] sm:min-w-[120px] text-center flex flex-col justify-center">
              <h3 className="text-lg font-semibold text-gray-900">{estimate.estimateNumber}</h3>
              <div className="mt-2 sm:hidden">
                <p className="text-sm text-gray-500">Expires</p>
                <p className="text-base font-medium text-gray-900">{formatInvoiceDate(estimate.expirationDate)}</p>
              </div>
            </div>

            <div className="flex-1 min-w-[45%] sm:min-w-[140px] text-center flex flex-col justify-center">
              <p className="text-base text-gray-600">{getCustomerName(estimate)}</p>
              <div className="mt-2 sm:hidden">
                <p className="text-sm text-gray-500">Amount</p>
                <p className="text-base font-medium text-gray-900">{formatCurrency(estimate.totalAmount)}</p>
              </div>
            </div>

            {/* Second Row on sm screens - Expiration Date and Amount */}
            <div className="hidden sm:flex sm:w-full sm:gap-4 md:w-auto md:contents">
              <div className="flex-1 min-w-[45%] md:min-w-[120px] flex flex-col items-center justify-center">
                <p className="text-sm text-gray-500">Expires</p>
                <p className="text-base font-medium text-gray-900">{formatInvoiceDate(estimate.expirationDate)}</p>
              </div>

              <div className="flex-1 min-w-[45%] md:min-w-[120px] flex flex-col items-center justify-center">
                <p className="text-sm text-gray-500">Amount</p>
                <p className="text-base font-medium text-gray-900">{formatCurrency(estimate.totalAmount)}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center ml-4">
            <div className="scale-110">
              {renderStatusBadge(estimate)}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => estimate.estimateNumber && navigate(`/merchant/estimates/${estimate.estimateNumber}`)}
            disabled={!estimate.estimateNumber}
            className="flex-1 px-4 py-2 bg-brand text-white rounded-lg font-semibold hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            View
          </button>
          {isDraft && estimate.estimateNumber && (
            <button
              onClick={() => navigate(`/merchant/estimates/${estimate.estimateNumber}/edit`)}
              className="flex-1 px-4 py-2 border border-brand text-gray-800 rounded-lg font-semibold hover:bg-brand-light transition-colors"
            >
              Edit
            </button>
          )}
          <div className="relative" ref={el => actionMenuRefs.current[estimate.id] = el}>
            <button
              onClick={() => setOpenActionMenuId(openActionMenuId === estimate.id ? null : estimate.id)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>
            {openActionMenuId === estimate.id && (
              <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                <div className="py-1">
                  <PermissionGate permission={PERMISSIONS.estimates.create}>
                    <button
                      onClick={() => { setOpenActionMenuId(null); handleDuplicateEstimate(estimate); }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50"
                    >
                      Duplicate Estimate
                    </button>
                  </PermissionGate>
                  {canConvert && (
                    <PermissionGate permission={PERMISSIONS.estimates.convert}>
                      <button
                        onClick={() => handleActionClick('convert', estimate)}
                        className="block w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50"
                      >
                        Convert to Invoice
                      </button>
                    </PermissionGate>
                  )}
                  <PermissionGate permission={PERMISSIONS.estimates.delete}>
                    <button
                      onClick={() => handleActionClick('delete', estimate)}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Delete Estimate
                    </button>
                  </PermissionGate>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render desktop estimate card
  const renderDesktopEstimateCard = (estimate) => {
    const isDraft = isEstimateEditable(estimate.status);
    const canConvert = canConvertToInvoice(estimate.status);

    return (
      <div key={estimate.id} className="bg-white rounded-lg shadow-sm border border-gray-200 py-3 px-6 hover:shadow-md transition-shadow">
        <div className="flex items-center">
          {/* Created date and Estimate number - 17% */}
          <div className="w-[17%] text-center px-2">
            <p className="text-xs text-gray-500 mb-1">Created: {formatInvoiceDate(estimate.estimateDate || estimate.issueDate)}</p>
            <Link
              to={`/merchant/estimates/${estimate.estimateNumber}`}
              className="text-sm font-semibold text-brand hover:text-brand-hover hover:underline truncate block"
            >
              {estimate.estimateNumber}
            </Link>
          </div>

          {/* Customer name + company - 22% */}
          <div className="w-[22%] text-center px-2">
            <p className="text-sm font-medium text-gray-900 truncate" title={getCustomerName(estimate)}>
              {getCustomerName(estimate)}
            </p>
            {getCustomerCompany(estimate) && (
              <p className="text-xs text-gray-500 truncate" title={getCustomerCompany(estimate)}>
                {getCustomerCompany(estimate)}
              </p>
            )}
          </div>

          {/* Expiration date - 14% */}
          <div className="w-[14%] text-center px-2">
            <p className="text-xs text-gray-500">Expires</p>
            <p className="text-sm font-medium text-gray-900">{formatInvoiceDate(estimate.expirationDate)}</p>
          </div>

          {/* Amount - 14% */}
          <div className="w-[14%] text-center px-2">
            <p className="text-xs text-gray-500">Amount</p>
            <p className="text-sm font-medium text-gray-900">{formatCurrency(estimate.totalAmount)}</p>
          </div>

          {/* Status - 12% */}
          <div className="w-[12%] flex justify-center px-2">
            {renderStatusBadge(estimate)}
          </div>

          {/* Actions - 21% */}
          <div className="w-[21%] flex items-center justify-end gap-3 px-2">
            {isDraft && estimate.estimateNumber && (
              <button
                onClick={() => navigate(`/merchant/estimates/${estimate.estimateNumber}/edit`)}
                className="text-sm text-brand hover:text-brand-hover font-medium whitespace-nowrap"
              >
                Edit
              </button>
            )}
            <button
              onClick={() => estimate.estimateNumber && navigate(`/merchant/estimates/${estimate.estimateNumber}`)}
              disabled={!estimate.estimateNumber}
              className="text-sm text-gray-700 hover:text-gray-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              View
            </button>
            <div className="relative" ref={el => actionMenuRefs.current[estimate.id] = el}>
              <button
                onClick={() => setOpenActionMenuId(openActionMenuId === estimate.id ? null : estimate.id)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
              {openActionMenuId === estimate.id && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                  <div className="py-1">
                    <PermissionGate permission={PERMISSIONS.estimates.create}>
                      <button
                        onClick={() => { setOpenActionMenuId(null); handleDuplicateEstimate(estimate); }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50"
                      >
                        Duplicate Estimate
                      </button>
                    </PermissionGate>
                    {canConvert && (
                      <PermissionGate permission={PERMISSIONS.estimates.convert}>
                        <button
                          onClick={() => handleActionClick('convert', estimate)}
                          className="block w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50"
                        >
                          Convert to Invoice
                        </button>
                      </PermissionGate>
                    )}
                    <PermissionGate permission={PERMISSIONS.estimates.delete}>
                      <button
                        onClick={() => handleActionClick('delete', estimate)}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        Delete Estimate
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && estimates.length === 0) {
    return (
      <div>
        {/* Skeleton statistics cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-100 rounded-lg p-4 h-20 animate-pulse" />
          ))}
        </div>
        {/* Skeleton filter row */}
        <div className="flex flex-wrap gap-4 mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-100 rounded-lg h-10 flex-1 min-w-[150px] animate-pulse" />
          ))}
        </div>
        {/* Skeleton estimate cards */}
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 h-16 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Statistics Cards - 4 across */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Draft */}
        <div className="bg-gray-100 rounded-lg p-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600 font-medium">{statistics.draftCount} Draft</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(statistics.draftAmount)}</p>
          </div>
          <button
            onClick={() => {
              setFilters({
                customerId: '',
                dateFrom: '',
                dateTo: '',
                status: 'draft',
                search: '',
                datePreset: ''
              });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            View
          </button>
        </div>

        {/* Sent */}
        <div className="bg-blue-100 rounded-lg p-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600 font-medium">{statistics.sentCount} Sent</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(statistics.sentAmount)}</p>
          </div>
          <button
            onClick={() => {
              setFilters({
                customerId: '',
                dateFrom: '',
                dateTo: '',
                status: 'sent',
                search: '',
                datePreset: ''
              });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            View
          </button>
        </div>

        {/* Accepted */}
        <div className="bg-green-100 rounded-lg p-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600 font-medium">{statistics.acceptedCount} Accepted</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(statistics.acceptedAmount)}</p>
          </div>
          <button
            onClick={() => {
              setFilters({
                customerId: '',
                dateFrom: '',
                dateTo: '',
                status: 'accepted',
                search: '',
                datePreset: ''
              });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            View
          </button>
        </div>

        {/* Win Rate */}
        <div className="bg-purple-100 rounded-lg p-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600 font-medium">Win Rate</p>
            <p className="text-2xl font-bold mt-1">{statistics.winRate}%</p>
          </div>
          <div className="flex items-center">
            <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        {/* Customer Filter */}
        <div className="relative flex-1 lg:flex-initial" ref={customerDropdownRef}>
          <button
            onClick={() => setIsCustomerDropdownOpen(!isCustomerDropdownOpen)}
            className="w-full lg:w-auto bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold flex items-center justify-between hover:bg-gray-300 transition-colors lg:min-w-[200px]"
          >
            {getFilterCustomerDisplay(filters.customerId)}
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isCustomerDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg py-1 max-h-60 overflow-auto">
              <button
                onClick={() => {
                  handleFilterChange('customerId', '');
                  setIsCustomerDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                All Customers
              </button>
              {Object.values(customers).map(customer => (
                <button
                  key={customer.id}
                  onClick={() => {
                    handleFilterChange('customerId', customer.id);
                    setIsCustomerDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {getCustomerDisplayName(customer)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Date Filter Dropdown */}
        <div className="relative flex-1 lg:flex-initial" ref={datePresetDropdownRef}>
          <button
            onClick={() => setIsDatePresetDropdownOpen(!isDatePresetDropdownOpen)}
            className="w-full lg:w-auto bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold flex items-center justify-between hover:bg-gray-300 transition-colors lg:min-w-[180px]"
          >
            {filters.datePreset ? (
              filters.datePreset === 'custom' ? 'Custom Dates' :
              filters.datePreset.charAt(0).toUpperCase() + filters.datePreset.slice(1)
            ) : 'All Dates'}
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isDatePresetDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg py-1">
              <button
                onClick={() => {
                  setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '', datePreset: '' }));
                  setIsDatePresetDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                All Dates
              </button>
              <button
                onClick={() => {
                  setShowCustomDatesModal(true);
                  setTempDateRange({ from: filters.dateFrom, to: filters.dateTo });
                  setIsDatePresetDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Custom Dates
              </button>
              {['today', 'week', 'month', 'quarter', 'year'].map(preset => (
                <button
                  key={preset}
                  onClick={() => {
                    handleDatePreset(preset);
                    setFilters(prev => ({ ...prev, datePreset: preset }));
                    setIsDatePresetDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {preset.charAt(0).toUpperCase() + preset.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Status Filter Dropdown */}
        <div className="relative flex-1 lg:flex-initial" ref={statusDropdownRef}>
          <button
            onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
            className="w-full lg:w-auto bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold flex items-center justify-between hover:bg-gray-300 transition-colors lg:min-w-[160px]"
          >
            {getStatusFilterDisplay(filters.status)}
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isStatusDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg py-1">
              <button
                onClick={() => {
                  handleFilterChange('status', '');
                  setIsStatusDropdownOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                All Statuses
              </button>
              {['draft', 'sent', 'accepted', 'declined', 'expired'].map(status => (
                <button
                  key={status}
                  onClick={() => {
                    handleFilterChange('status', status);
                    setIsStatusDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Search Box */}
        <div className="relative flex-1 lg:flex-initial lg:min-w-[250px]">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Search estimates..."
            className="w-full pl-10 pr-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-medium placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </div>

        {/* Date Range Display - Only show if dates are selected */}
        {(filters.dateFrom || filters.dateTo) && (
          <div className="hidden lg:flex items-center gap-2 text-sm text-gray-600">
            <span>{filters.dateFrom || 'mm/dd/yyyy'}</span>
            <span className="text-gray-500">to</span>
            <span>{filters.dateTo || 'mm/dd/yyyy'}</span>
          </div>
        )}
      </div>

      {/* Date Range Display for Mobile/Tablet - Show below filters if dates are selected */}
      {(filters.dateFrom || filters.dateTo) && (
        <div className="flex lg:hidden items-center gap-2 text-sm text-gray-600 mb-4">
          <span>{filters.dateFrom || 'mm/dd/yyyy'}</span>
          <span className="text-gray-500">to</span>
          <span>{filters.dateTo || 'mm/dd/yyyy'}</span>
        </div>
      )}

      {/* Custom Dates Modal */}
      <Transition.Root show={showCustomDatesModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowCustomDatesModal(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95">
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all w-full max-w-md">
                  <div className="px-6 py-4 border-b bg-gray-50">
                    <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">Select Date Range</Dialog.Title>
                  </div>
                  <div className="px-6 py-5 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                      <input
                        type="date"
                        value={tempDateRange.from}
                        onChange={(e) => setTempDateRange(prev => ({ ...prev, from: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                      <input
                        type="date"
                        value={tempDateRange.to}
                        onChange={(e) => setTempDateRange(prev => ({ ...prev, to: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
                    <button
                      onClick={() => {
                        setTempDateRange({ from: '', to: '' });
                        setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '', datePreset: '' }));
                        setShowCustomDatesModal(false);
                      }}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Reset
                    </button>
                    <button
                      onClick={() => {
                        setFilters(prev => ({
                          ...prev,
                          dateFrom: tempDateRange.from,
                          dateTo: tempDateRange.to,
                          datePreset: 'custom'
                        }));
                        setShowCustomDatesModal(false);
                      }}
                      className="px-4 py-2 text-sm bg-brand text-gray-800 rounded-lg font-medium hover:bg-brand-hover transition-colors"
                    >
                      Apply
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Confirmation Modal (Convert / Delete) */}
      <Transition.Root show={showConfirmModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => {
          if (!actionLoading) {
            setShowConfirmModal(false);
            setSelectedEstimate(null);
            setConfirmAction(null);
          }
        }}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95">
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all w-full max-w-md">
                  <div className="px-6 py-4 border-b bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${confirmAction === 'convert' ? 'bg-purple-100' : 'bg-red-100'}`}>
                        {confirmAction === 'convert' ? (
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        )}
                      </div>
                      <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                        {confirmAction === 'convert' ? 'Convert to Invoice' : 'Delete Estimate'}
                      </Dialog.Title>
                    </div>
                  </div>
                  <div className="px-6 py-5">
                    <p className="text-sm text-gray-600">
                      {confirmAction === 'convert'
                        ? 'This will create a new invoice from this estimate and mark it as converted. Continue?'
                        : `Are you sure you want to delete estimate ${selectedEstimate?.estimateNumber}? This action cannot be undone.`
                      }
                    </p>
                  </div>
                  <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
                    <button
                      onClick={() => {
                        setShowConfirmModal(false);
                        setSelectedEstimate(null);
                        setConfirmAction(null);
                      }}
                      disabled={actionLoading}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmAction}
                      disabled={actionLoading}
                      className={`px-4 py-2 text-sm rounded-lg font-medium disabled:opacity-50 transition-colors ${
                        confirmAction === 'convert'
                          ? 'bg-purple-600 text-white hover:bg-purple-500'
                          : 'bg-red-600 text-white hover:bg-red-500'
                      }`}
                    >
                      {actionLoading ? (
                        <span className="flex items-center gap-2">
                          <Spinner />
                          Processing...
                        </span>
                      ) : (
                        confirmAction === 'convert' ? 'Convert to Invoice' : 'Delete Estimate'
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
          <button
            onClick={() => setError(null)}
            className="float-right text-red-700 hover:text-red-900 font-bold"
          >
            &times;
          </button>
        </div>
      )}

      {/* Estimates Display - Table for desktop, Cards for mobile */}
      {estimates.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900">No estimates found</h3>
          <p className="mt-1 text-sm text-gray-500">Create your first estimate to get started.</p>
          <PermissionGate permission={PERMISSIONS.estimates.create}>
            <Link
              to="/merchant/estimates/new"
              className="mt-4 inline-flex items-center px-4 py-2 bg-brand text-white rounded-lg font-semibold hover:bg-brand-hover transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Create Estimate
            </Link>
          </PermissionGate>
        </div>
      ) : (
        <>
          {/* Mobile View - Cards */}
          <div className="block lg:hidden">
            {estimates.map(estimate => renderMobileEstimateCard(estimate))}
          </div>

          {/* Desktop View - Cards */}
          <div className="hidden lg:block">
            <div className="space-y-4">
              {estimates.map(estimate => renderDesktopEstimateCard(estimate))}
            </div>

            {/* Desktop Pagination - only show if more than 25 items */}
            {pagination.totalItems > 25 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-4 p-4">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-700 text-center lg:text-left">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.totalItems)} of {pagination.totalItems} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.totalPages}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Pagination - only show if more than 25 items */}
          {pagination.totalItems > 25 && (
            <div className="block lg:hidden mt-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-700 text-center mb-3">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.totalItems)} of {pagination.totalItems} results
                </div>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default EstimateList;
